import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useEventLogger from './useEventLogger'
import { timeout } from '../../utilities/helpers'

const AUTO_OPEN_DELAY_MS = 700

type AutoOpenLocation = ReturnType<typeof useLocation> & {
    autoOpenData?: {
        isAutoOpenPanel?: boolean
        autoOpenData?: { _id?: string }
        previousDataStack?: unknown[]
    } | null
}

export const useTableDetailPanel = <TRow extends { _id: string }>(rows: TRow[], viewedType: string) => {
    const location = useLocation() as AutoOpenLocation
    const { logEvent, ACTIONS } = useEventLogger()

    const [openRowId, setOpenRowId] = useState<string | null>(null)
    const [navigationStack, setNavigationStack] = useState<unknown[]>([])

    const openPanel = useCallback(
        (rowId: string) => {
            logEvent(ACTIONS.VIEW, { type: viewedType, _id: rowId })
            setOpenRowId(rowId)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewedType]
    )

    const closePanel = useCallback(() => {
        setOpenRowId(null)
        setNavigationStack([])
        location.autoOpenData = null
    }, [location])

    useEffect(() => {
        const { autoOpenData } = location

        if (!autoOpenData?.isAutoOpenPanel || !autoOpenData.autoOpenData?._id) return

        const rowId = autoOpenData.autoOpenData._id

        if (!rows.some((row) => row._id === rowId)) return

        let isCancelled = false

        setNavigationStack(autoOpenData.previousDataStack ?? [])
        timeout(AUTO_OPEN_DELAY_MS).then(() => {
            if (!isCancelled) openPanel(rowId)
        })

        return () => {
            isCancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.autoOpenData, rows])

    return {
        openRow: rows.find((row) => row._id === openRowId) ?? null,
        navigationStack,
        openPanel,
        closePanel,
    }
}
